import React from 'react';

// Import all translation files
import { englishTranslations } from './translations-en.js';
import { hebrewTranslations } from './translations-he.js';
import { arabicTranslations } from './translations-ar.js';
import { frenchTranslations } from './translations-fr.js';
import { germanTranslations } from './translations-de.js';
import { russianTranslations } from './translations-ru.js';

// Combined translations object for full backward compatibility
export const translations = {
  en: englishTranslations,
  he: hebrewTranslations,
  ar: arabicTranslations,
  fr: frenchTranslations,
  de: germanTranslations,
  ru: russianTranslations
};

// Language configuration with all supported languages
export const languageConfig = {
  en: {
    name: 'English',
    flag: '🇺🇸',
    dir: 'ltr',
    nativeName: 'English'
  },
  he: {
    name: 'Hebrew',
    flag: '🇮🇱',
    dir: 'rtl',
    nativeName: 'עברית'
  },
  ar: {
    name: 'Arabic',
    flag: '🇸🇦',
    dir: 'rtl',
    nativeName: 'العربية'
  },
  fr: {
    name: 'French',
    flag: '🇫🇷',
    dir: 'ltr',
    nativeName: 'Français'
  },
  de: {
    name: 'German',
    flag: '🇩🇪',
    dir: 'ltr',
    nativeName: 'Deutsch'
  },
  ru: {
    name: 'Russian',
    flag: '🇷🇺',
    dir: 'ltr',
    nativeName: 'Русский'
  }
};

// Get list of all available languages for dropdowns/selectors
export const getAvailableLanguages = () => {
  return Object.entries(languageConfig).map(([code, config]) => ({
    code,
    ...config
  }));
};

// Enhanced translation utility functions
export const getTranslation = (key, language = 'en', fallback = null) => {
  const translation = translations[language]?.[key];
  if (translation) return translation;

  // Fallback to English if translation not found in selected language
  const englishTranslation = translations.en?.[key];
  if (englishTranslation) return englishTranslation;

  // Return custom fallback or the key itself
  return fallback || key;
};

export const isRTL = (language) => {
  return languageConfig[language]?.dir === 'rtl';
};

// Enhanced RTL initialization utility
export const initializeRTL = (language) => {
  const isRtl = isRTL(language);
  const htmlElement = document.documentElement;

  // Set direction and language attributes
  htmlElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
  htmlElement.setAttribute('lang', language);

  // Apply or remove RTL class
  if (isRtl) {
    htmlElement.classList.add('rtl-layout');
  } else {
    htmlElement.classList.remove('rtl-layout');
  }

  // Save language preference
  localStorage.setItem('preferred-language', language);

  console.log('🌐 Language initialized:', {
    language,
    isRtl,
    direction: htmlElement.getAttribute('dir'),
    hasRtlClass: htmlElement.classList.contains('rtl-layout')
  });

  return isRtl;
};

// Utility hook for language management in components
export const useLanguageSetup = (propLanguage, propT) => {
  const [currentLanguage, setCurrentLanguage] = React.useState(() => {
    if (propLanguage) return propLanguage;
    const savedLanguage = localStorage.getItem('preferred-language');
    // Validate saved language exists in config
    if (savedLanguage && languageConfig[savedLanguage]) {
      return savedLanguage;
    }
    return 'en';
  });

  const t = React.useCallback((key, fallback = null) => {
    if (propT) return propT(key, fallback);
    return getTranslation(key, currentLanguage, fallback);
  }, [propT, currentLanguage]);

  React.useEffect(() => {
    initializeRTL(currentLanguage);
  }, [currentLanguage]);

  React.useEffect(() => {
    if (propLanguage && propLanguage !== currentLanguage) {
      setCurrentLanguage(propLanguage);
    }
  }, [propLanguage]);

  return { currentLanguage, setCurrentLanguage, t };
};

// Level system specific utility functions
export const getLevelTitle = (level, language = 'en') => {
  const levelTitles = {
    en: {
      1: 'Beginner', 10: 'Novice', 20: 'Intermediate', 30: 'Advanced',
      40: 'Professional', 50: 'Expert', 60: 'Master', 70: 'Grandmaster',
      80: 'Mythic', 90: 'Sage', 100: 'Legend'
    },
    he: {
      1: 'מתחיל', 10: 'טירון', 20: 'בינוני', 30: 'מתקדם',
      40: 'מקצועי', 50: 'מומחה', 60: 'אמן', 70: 'אמן גדול',
      80: 'מיתי', 90: 'חכם', 100: 'אגדה'
    },
    ar: {
      1: 'مبتدئ', 10: 'مستجد', 20: 'متوسط', 30: 'متقدم',
      40: 'محترف', 50: 'خبير', 60: 'سيد', 70: 'سيد أعظم',
      80: 'أسطوري', 90: 'حكيم', 100: 'أسطورة'
    },
    fr: {
      1: 'Débutant', 10: 'Novice', 20: 'Intermédiaire', 30: 'Avancé',
      40: 'Professionnel', 50: 'Expert', 60: 'Maître', 70: 'Grand Maître',
      80: 'Mythique', 90: 'Sage', 100: 'Légende'
    },
    de: {
      1: 'Anfänger', 10: 'Neuling', 20: 'Fortgeschritten', 30: 'Erfahren',
      40: 'Profi', 50: 'Experte', 60: 'Meister', 70: 'Großmeister',
      80: 'Mythisch', 90: 'Weiser', 100: 'Legende'
    },
    ru: {
      1: 'Новичок', 10: 'Начинающий', 20: 'Средний', 30: 'Продвинутый',
      40: 'Профессионал', 50: 'Эксперт', 60: 'Мастер', 70: 'Гроссмейстер',
      80: 'Мифический', 90: 'Мудрец', 100: 'Легенда'
    }
  };

  // Find the appropriate title for the level
  const titles = levelTitles[language] || levelTitles.en;
  const levelKeys = Object.keys(titles).map(Number).sort((a, b) => b - a);

  for (const levelKey of levelKeys) {
    if (level >= levelKey) {
      return titles[levelKey];
    }
  }

  return titles[1]; // Default to beginner
};

export const getBadgeCategoryName = (category, language = 'en') => {
  const categoryNames = {
    en: {
      'win_streak': 'Win Streak',
      'filler': 'Filler',
      'total_wins': 'Total Wins',
      'points_winning': 'Points Winning',
      'points_purchase': 'Points Purchase',
      'multiplayer': 'Multiplayer',
      'battle_royale': 'Battle Royale',
      'multiplayer_wins': 'Multiplayer Wins',
      'battle_royale_wins': 'Battle Royale Wins'
    },
    he: {
      'win_streak': 'רצף ניצחונות',
      'filler': 'מילוי',
      'total_wins': 'סך ניצחונות',
      'points_winning': 'נקודות מניצחונות',
      'points_purchase': 'רכישת נקודות',
      'multiplayer': 'מולטי פלייר',
      'battle_royale': 'בטל רויאל',
      'multiplayer_wins': 'ניצחונות מולטי פלייר',
      'battle_royale_wins': 'ניצחונות בטל רויאל'
    },
    ar: {
      'win_streak': 'سلسلة الانتصارات',
      'filler': 'ملء',
      'total_wins': 'إجمالي الانتصارات',
      'points_winning': 'نقاط الفوز',
      'points_purchase': 'شراء النقاط',
      'multiplayer': 'متعدد اللاعبين',
      'battle_royale': 'باتل رويال',
      'multiplayer_wins': 'انتصارات متعدد اللاعبين',
      'battle_royale_wins': 'انتصارات باتل رويال'
    },
    fr: {
      'win_streak': 'Série de victoires',
      'filler': 'Remplisseur',
      'total_wins': 'Total des victoires',
      'points_winning': 'Points gagnés',
      'points_purchase': 'Achat de points',
      'multiplayer': 'Multijoueur',
      'battle_royale': 'Battle Royale',
      'multiplayer_wins': 'Victoires multijoueur',
      'battle_royale_wins': 'Victoires Battle Royale'
    },
    de: {
      'win_streak': 'Siegesserie',
      'filler': 'Füller',
      'total_wins': 'Gesamte Siege',
      'points_winning': 'Punkte gewinnen',
      'points_purchase': 'Punkte kaufen',
      'multiplayer': 'Mehrspieler',
      'battle_royale': 'Battle Royale',
      'multiplayer_wins': 'Mehrspieler-Siege',
      'battle_royale_wins': 'Battle Royale-Siege'
    },
    ru: {
      'win_streak': 'Серия побед',
      'filler': 'Наполнитель',
      'total_wins': 'Всего побед',
      'points_winning': 'Выигрыш очков',
      'points_purchase': 'Покупка очков',
      'multiplayer': 'Мультиплеер',
      'battle_royale': 'Королевская битва',
      'multiplayer_wins': 'Победы в мультиплеере',
      'battle_royale_wins': 'Победы в королевской битве'
    }
  };

  return categoryNames[language]?.[category] || categoryNames.en?.[category] || category;
};

export const getRarityName = (rarity, language = 'en') => {
  const rarityNames = {
    en: { 'common': 'Common', 'rare': 'Rare', 'epic': 'Epic', 'legendary': 'Legendary' },
    he: { 'common': 'רגיל', 'rare': 'נדיר', 'epic': 'אפי', 'legendary': 'אגדי' },
    ar: { 'common': 'عادي', 'rare': 'نادر', 'epic': 'ملحمي', 'legendary': 'أسطوري' },
    fr: { 'common': 'Commun', 'rare': 'Rare', 'epic': 'Épique', 'legendary': 'Légendaire' },
    de: { 'common': 'Gewöhnlich', 'rare': 'Selten', 'epic': 'Episch', 'legendary': 'Legendär' },
    ru: { 'common': 'Обычный', 'rare': 'Редкий', 'epic': 'Эпический', 'legendary': 'Легендарный' }
  };

  return rarityNames[language]?.[rarity] || rarityNames.en?.[rarity] || rarity;
};

// Add these utility functions for time formatting in cleanup contexts
export const formatCleanupTime = (milliseconds, language = 'en') => {
  const minutes = Math.floor(milliseconds / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  const t = (key, fallback) => getTranslation(key, language, fallback);

  if (days > 0) {
    return `${days} ${t('days', 'days')}`;
  } else if (hours > 0) {
    return `${hours} ${t('hours', 'hours')}`;
  } else {
    return `${minutes} ${t('minutes', 'minutes')}`;
  }
};

export const formatCleanupAge = (createdAt, language = 'en') => {
  const now = new Date();
  const created = new Date(createdAt);
  const diffMs = now - created;

  return formatCleanupTime(diffMs, language);
};

export const getTimeUntilCleanup = (createdAt, maxAgeHours = 1, language = 'en') => {
  const now = new Date();
  const created = new Date(createdAt);
  const maxAgeMs = maxAgeHours * 60 * 60 * 1000;
  const ageMs = now - created;
  const remainingMs = maxAgeMs - ageMs;

  if (remainingMs <= 0) {
    return { expired: true, timeRemaining: 0 };
  }

  return {
    expired: false,
    timeRemaining: remainingMs,
    formatted: formatCleanupTime(remainingMs, language)
  };
};

// Enhanced tournament age indicator component
export const TournamentAgeIndicator = ({ tournament, language, className = '' }) => {
  const t = (key, fallback) => getTranslation(key, language, fallback);
  const age = formatCleanupAge(tournament.createdAt, language);
  const cleanup = getTimeUntilCleanup(tournament.createdAt, 1, language);

  if (cleanup.expired) {
    return (
      <div className={`tournament-age-indicator expired ${className}`}>
        <span className="age-icon">⏰</span>
        <span className="age-text">{t('tournamentAutoDeleted', 'Tournament automatically deleted due to age')}</span>
      </div>
    );
  }

  const isWarning = cleanup.timeRemaining < 15 * 60 * 1000; // 15 minutes
  const isCritical = cleanup.timeRemaining < 5 * 60 * 1000; // 5 minutes

  return (
    <div className={`tournament-age-indicator ${isWarning ? 'warning' : ''} ${isCritical ? 'critical' : ''} ${className}`}>
      <span className="age-icon">⏰</span>
      <span className="age-text">
        {t('tournamentAgeInfo', 'Tournament age: {age}').replace('{age}', age)}
      </span>
      {isWarning && (
        <span className="cleanup-warning">
          {t('autoDeleteIn', 'Auto-delete in')} {cleanup.formatted}
        </span>
      )}
    </div>
  );
};

// CSS for the age indicator (add to your CSS file)
export const ageIndicatorStyles = `
.tournament-age-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #9ca3af;
  margin-top: 2px;
}

.tournament-age-indicator .age-icon {
  font-size: 8px;
}

.tournament-age-indicator.warning {
  color: #f59e0b;
}

.tournament-age-indicator.critical {
  color: #ef4444;
  animation: pulse 1s ease-in-out infinite;
}

.tournament-age-indicator .cleanup-warning {
  font-weight: bold;
  margin-left: 4px;
}

.tournament-age-indicator.expired {
  color: #ef4444;
  font-weight: bold;
}

@media (max-width: 480px) {
  .tournament-age-indicator {
    font-size: 9px;
  }

  .tournament-age-indicator .cleanup-warning {
    display: none; /* Hide warning text on very small screens */
  }
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}
`;

// Export individual translation objects for direct access if needed
export {
  englishTranslations,
  hebrewTranslations,
  arabicTranslations,
  frenchTranslations,
  germanTranslations,
  russianTranslations
};

// Default export for backward compatibility
export default translations;
